// Step types (classify labels) read as time: the bars on the Steps tab and in a step's details,
// and the chips under a step's name.
import { Shapes } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { INSIGNIFICANT_COLOR } from "../analytics/palette";
import AppUsageBars from "./AppUsageBars";
import {
  type Categories,
  type StepTypeShare,
  UNCLASSIFIED,
  categoryGroup,
  categoryName,
} from "./processGroups";
import { CollapsibleSidebarSection } from "./processSidebarUi";

const CHIP_LIMIT = 2;

/**
 * Bars per step type, scaled to the leading one; `onSelect` makes the classified rows clickable,
 * `onPreview` reports the row pointed at (null on leaving).
 */
export const StepTypeBars = ({
  shares,
  categories,
  onSelect,
  onPreview,
}: {
  shares: StepTypeShare[];
  categories: Categories;
  onSelect?: (id: string) => void;
  onPreview?: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  // A handful of rows: full panel width stretches the bars past easy comparison.
  return (
    <div className="mx-auto max-w-md -translate-x-3">
      <AppUsageBars
        rows={shares.map(share => {
          const { icon: Icon, tone } = categoryGroup(share.id, categories);
          const unclassified = share.id === UNCLASSIFIED;
          return {
            name: categoryName(share.id, categories, t),
            seconds: share.seconds,
            pct: share.pct,
            color: INSIGNIFICANT_COLOR,
            iconUri: null,
            icon: (
              <span className={`flex h-5 w-5 items-center justify-center rounded ${tone}`}>
                <Icon aria-hidden="true" className="h-3.5 w-3.5" />
              </span>
            ),
            isOther: unclassified,
            onClick: onSelect && !unclassified ? () => onSelect(share.id) : undefined,
            onHover: onPreview && ((hovering: boolean) => onPreview(hovering ? share.id : null)),
          };
        })}
      />
    </div>
  );
};

/** The step's types as chips, most time first; the rest fold into "+N". */
export const StepTypeChips = ({
  shares,
  categories,
}: {
  shares: StepTypeShare[];
  categories: Categories;
}) => {
  const { t } = useTranslation();
  const types = shares.filter(share => share.id !== UNCLASSIFIED);
  if (types.length === 0) return null;
  const hidden = types.slice(CHIP_LIMIT);
  return (
    <span className="mt-1.5 flex flex-wrap gap-1">
      {types.slice(0, CHIP_LIMIT).map(share => {
        const { icon: Icon, tone } = categoryGroup(share.id, categories);
        return (
          <span
            key={share.id}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
          >
            <Icon aria-hidden="true" className="h-3 w-3" />
            {categoryName(share.id, categories, t)}
          </span>
        );
      })}
      {hidden.length > 0 && (
        <span
          title={hidden.map(share => categoryName(share.id, categories, t)).join(", ")}
          className="inline-flex items-center rounded-full bg-surface-strong px-2 py-0.5 text-[11px] font-medium text-text-mid"
        >
          +{hidden.length}
        </span>
      )}
    </span>
  );
};

/** A step's time per type, collapsed to its leading type like App usage. */
export const ProcessStepTypesSection = ({
  shares,
  categories,
}: {
  shares: StepTypeShare[];
  categories: Categories;
}) => {
  const { t } = useTranslation();
  const lead = shares[0];
  // A single row is always 100%: nothing to compare.
  if (shares.length < 2) return null;
  return (
    <CollapsibleSidebarSection
      title={t("processPanel.stepTypes")}
      headerIcon={<Shapes className="h-4 w-4" aria-hidden="true" />}
      summary={t("processPanel.stepTypesSummary", {
        type: categoryName(lead.id, categories, t),
        pct: Math.round(lead.pct),
      })}
      defaultOpen={false}
      separated={false}
      className="border-t border-outline pt-4"
    >
      <StepTypeBars shares={shares} categories={categories} />
    </CollapsibleSidebarSection>
  );
};
